<template>
  <div class="app-layout">
    <el-container style="height: 100vh;">
      <!-- 左侧菜单 -->
      <el-aside :width="isCollapse ? '64px' : '220px'" class="layout-aside">
        <div class="logo-container">
          <span class="logo" v-show="!isCollapse">offer管家</span>
          <span class="logo-mini" v-show="isCollapse">offer</span>
        </div>
        
        <!-- 折叠按钮 -->
        <div class="collapse-btn" @click="toggleCollapse">
          <i :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
        </div>
        
        <!-- 动态菜单 -->
        <dynamic-menu :is-collapse="isCollapse" 
                      background-color="transparent"
                      text-color="#b8c5d6"
                      active-text-color="#4facfe" />
      </el-aside>

      <el-container>
        <el-header class="layout-header">
          <div class="header-left">
            <span class="current-page">{{ currentPageTitle }}</span>
          </div>
          <!-- 全局冻结提示条 -->
          <el-alert
            v-if="reconciliationFrozen"
            title="结账冻结期：已投数已锁定，非超管用户无法修改"
            type="warning"
            :closable="false"
            show-icon
            class="freeze-banner"
          />
          <!-- 核算期提示条 -->
          <el-alert
            v-if="accountingPeriod.period"
            :title="`核算期进行中：${accountingPeriod.period}月，修改数据需确认类型`"
            type="info"
            :closable="false"
            show-icon
            class="accounting-period-banner"
          />
          <div class="header-right">
            <!-- 消息通知 -->
            <el-popover
              placement="bottom-end"
              width="300"
              trigger="hover"
              popper-class="notification-popover"
            >
              <div class="notification-header">
                <span>消息通知</span>
                <el-link type="primary" :underline="false" @click="markAllRead" v-if="unreadCount > 0">全部已读</el-link>
              </div>
              <div class="notification-list" v-if="notifications.length > 0">
                <div 
                  v-for="item in notifications" 
                  :key="item.id" 
                  class="notification-item"
                  :class="{ unread: !item.isRead }"
                  @click="handleNotificationClick(item)"
                >
                  <div class="notification-icon" :class="item.type">
                    <i :class="getNotificationIcon(item.type)"></i>
                  </div>
                  <div class="notification-content">
                    <div class="notification-title">{{ item.title }}</div>
                    <div class="notification-desc">{{ item.content }}</div>
                    <div class="notification-time">{{ formatTime(item.createdAt) }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="notification-empty">
                <i class="el-icon-bell"></i>
                <span>暂无消息</span>
              </div>
              <div class="notification-footer" v-if="notifications.length > 0">
                <el-link type="primary" :underline="false" @click="$router.push('/notifications')">查看全部</el-link>
              </div>
              <el-badge slot="reference" :value="unreadCount" :hidden="unreadCount === 0" class="notification-badge">
                <i class="el-icon-bell notification-icon-btn" :class="{ active: unreadCount > 0 }"></i>
              </el-badge>
            </el-popover>
            
            <el-dropdown @command="handleUserCommand" class="user-dropdown">
              <span class="user-info">
                <i class="el-icon-user"></i>
                {{ user.name }} <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>
        
        <el-main class="layout-main">
          <router-view />
        </el-main>
      </el-container>
    </el-container>
    
    <!-- 密码修改弹窗 -->
    <change-password-dialog :visible.sync="changePasswordVisible" />
  </div>
</template>

<script>
import DynamicMenu from '@/components/DynamicMenu.vue'
import ChangePasswordDialog from '@/components/ChangePasswordDialog.vue'
import socketService from '@/services/socketService'
import { notificationApi } from '@/api'

export default {
  name: 'Layout',
  components: {
    DynamicMenu,
    ChangePasswordDialog
  },
  data() {
    return {
      isCollapse: false,
      notifications: [],
      unreadCount: 0,
      notificationTimer: null,
      hasNotificationPermission: false,
      changePasswordVisible: false
    }
  },
  mounted() {
    // 请求浏览器桌面通知权限
    this.requestNotificationPermission()
    
    // 获取初始通知数据
    this.fetchNotifications()
    
    // 监听 WebSocket 实时通知
    this.setupSocketListener()
  },
  beforeDestroy() {
    // 移除 Socket 监听
    this.removeSocketListener()
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    currentPageTitle() {
      return this.$route.meta?.title || '首页'
    },
    // 从 store 获取未读数，实现跨组件同步
    storeUnreadCount() {
      return this.$store.getters.unreadNotifications
    },
    reconciliationFrozen() {
      return this.$store.getters.reconciliationFrozen
    },
    accountingPeriod() {
      return this.$store.getters.accountingPeriod || {}
    }
  },
  watch: {
    // 监听 store 未读数变化，更新本地显示
    storeUnreadCount: {
      immediate: true,
      handler(newVal) {
        this.unreadCount = newVal
        // 收到新通知时显示桌面弹窗
        if (newVal > 0 && this.notifications.length > 0) {
          const latest = this.notifications[0]
          this.showDesktopNotification(latest)
        }
      }
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapse = !this.isCollapse
    },
    handleUserCommand(command) {
      if (command === 'logout') {
        this.$store.dispatch('logout')
        this.$router.push('/login')
      } else if (command === 'changePassword') {
        this.changePasswordVisible = true
      }
    },
    async fetchNotifications() {
      try {
        const res = await notificationApi.getList({
          page: 1,
          pageSize: 5,
          isRead: false
        })
        if (res.code === 200) {
          this.notifications = res.data.list || []
          // 同时更新 store 中的未读数
          const unreadRes = await notificationApi.getUnreadCount()
          if (unreadRes.code === 200) {
            this.$store.commit('SET_UNREAD_NOTIFICATIONS', unreadRes.data.count)
          }
        }
      } catch (error) {
        console.error('获取通知失败:', error)
      }
    },
    getNotificationIcon(type) {
      const iconMap = {
        order: 'el-icon-s-order',
        assignment: 'el-icon-s-claim',
        system: 'el-icon-s-tools',
        message: 'el-icon-message'
      }
      return iconMap[type] || 'el-icon-bell'
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
      return `${Math.floor(diff / 86400000)}天前`
    },

    async handleNotificationClick(item) {
      if (!item.isRead) {
        try {
          await notificationApi.markAsRead(item.id)
          item.isRead = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
          this.$store.commit('SET_UNREAD_NOTIFICATIONS', this.unreadCount)
        } catch (error) {
          console.error('标记已读失败:', error)
        }
      }
      
      // 根据通知类型跳转到对应页面
      if (item.data?.agentId) {
        // 连续逾期预警等代投员维度通知 → 运营分析页定位到该代投员
        this.$router.push({ path: '/data/analysis', query: { agent_id: item.data.agentId } })
      } else {
        this.$router.push('/notifications')
      }
    },
    async markAllRead() {
      try {
        await notificationApi.markAllAsRead()
        this.notifications.forEach(item => item.isRead = true)
        this.unreadCount = 0
        this.$store.commit('SET_UNREAD_NOTIFICATIONS', 0)
        this.$message.success('已全部标记为已读')
      } catch (error) {
        console.error('标记全部已读失败:', error)
        // request.js 拦截器已统一提示错误
      }
    },
    requestNotificationPermission() {
      if ('Notification' in window) {
        Notification.requestPermission().then(permission => {
          this.hasNotificationPermission = permission === 'granted'
        })
      }
    },
    setupSocketListener() {
      socketService.onNewNotification(this.onNewNotification)
    },
    removeSocketListener() {
      socketService.offNewNotification(this.onNewNotification)
    },
    showDesktopNotification(notification) {
      if (!this.hasNotificationPermission || Notification.permission !== 'granted') {
        return
      }
      
      const notif = new Notification(notification.title, {
        body: notification.content,
        icon: '/favicon.ico'
      })
      
      notif.onclick = () => {
        window.focus()
        this.handleNotificationClick(notification)
        notif.close()
      }
    },
    onNewNotification(data) {
      // 将新通知添加到列表顶部
      this.notifications.unshift(data)
      // 保持最多5条
      if (this.notifications.length > 5) {
        this.notifications.pop()
      }
      // 显示桌面通知
      this.showDesktopNotification(data)
    }
  }
}
</script>

<style scoped>
.freeze-banner,
.accounting-period-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  border-radius: 0;
  padding: 8px 20px;
}

.accounting-period-banner {
  top: 40px; /* 如果有冻结提示条，核算期提示条在其下方 */
}

.layout-header {
  position: relative;
}
.layout-aside {
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  transition: width 0.3s;
}

.logo-container {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  letter-spacing: 2px;
}

.logo-mini {
  font-size: 20px;
  font-weight: bold;
  color: #4facfe;
}

.collapse-btn {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #b8c5d6;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s;
}

.collapse-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #4facfe;
}

.layout-header {
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}

.header-left {
  font-size: 16px;
  font-weight: 500;
  color: #1a1a2e;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.notification-badge {
  cursor: pointer;
}

.notification-icon-btn {
  font-size: 22px;
  color: #909399;
  transition: all 0.3s;
  padding: 8px;
  border-radius: 4px;
}

.notification-icon-btn:hover {
  color: #4facfe;
  background: #f5f7fa;
}

.notification-icon-btn.active {
  color: #f56c6c;
  animation: bell-shake 1s ease-in-out infinite;
}

@keyframes bell-shake {
  0%, 100% { transform: rotate(0deg); }
  10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
  20%, 40%, 60%, 80% { transform: rotate(10deg); }
}

.user-dropdown {
  margin-left: 10px;
}

.user-info {
  cursor: pointer;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s;
}

.user-info:hover {
  background: #f5f7fa;
  color: #4facfe;
}

.user-info i {
  color: #909399;
}

.layout-main {
  background-color: #f5f7fa;
  padding: 20px;
  overflow-y: auto;
}

/* 通知弹窗样式 */
.notification-popover {
  padding: 0 !important;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  font-size: 14px;
}

.notification-list {
  max-height: 360px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 1px solid #f0f0f0;
}

.notification-item:hover {
  background-color: #f5f7fa;
}

.notification-item.unread {
  background-color: #f0f9ff;
}

.notification-item.unread:hover {
  background-color: #e6f7ff;
}

.notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.notification-icon.order {
  background-color: #e6f7ff;
  color: #1890ff;
}

.notification-icon.assignment {
  background-color: #f6ffed;
  color: #52c41a;
}

.notification-icon.system {
  background-color: #fff2e8;
  color: #fa8c16;
}

.notification-icon.message {
  background-color: #f9f0ff;
  color: #722ed1;
}

.notification-icon i {
  font-size: 16px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 500;
  font-size: 14px;
  color: #303133;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-desc {
  font-size: 12px;
  color: #606266;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  font-size: 12px;
  color: #909399;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #909399;
}

.notification-empty i {
  font-size: 40px;
  margin-bottom: 12px;
  color: #dcdfe6;
}

.notification-footer {
  padding: 12px 16px;
  text-align: center;
  border-top: 1px solid #ebeef5;
}
</style>
